import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Smartphone, Loader2 } from "lucide-react";

const ScoreChip = ({ label, v }) => (
  <div className="px-2.5 py-1 rounded-full bg-stone-100 text-xs font-bold flex items-center gap-1.5">
    <span className="text-stone-500 font-semibold">{label}</span>
    <span className={v >= 70 ? "text-emerald-700" : v >= 40 ? "text-amber-700" : "text-red-600"}>{v}</span>
  </div>
);

export default function OneChatPanel({ clientId }) {
  const [info, setInfo] = useState(null);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [creating, setCreating] = useState(false);
  const bottomRef = useRef(null);

  const loadInfo = useCallback(async () => {
    try {
      const r = await api.get(`/agent/one-clients/by-client/${clientId}`);
      setInfo(r.data);
      if (!r.data.linked) setForm((f) => ({ ...f, email: r.data.client.email || "" }));
    } catch { toast.error("Failed to load Crece One status"); }
  }, [clientId]);
  useEffect(() => { loadInfo(); }, [loadInfo]);

  const oneId = info?.linked ? info.customer.id : null;
  const loadThread = useCallback(async () => {
    if (!oneId) return;
    try { setThread((await api.get(`/agent/one-clients/${oneId}/messages`)).data.messages); } catch { /* poll */ }
  }, [oneId]);

  useEffect(() => {
    if (!oneId) return;
    loadThread();
    const t = setInterval(loadThread, 5000);
    return () => clearInterval(t);
  }, [oneId, loadThread]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread.length]);

  const send = async () => {
    if (!text.trim() || !oneId) return;
    setSending(true);
    try {
      await api.post(`/agent/one-clients/${oneId}/messages`, { text: text.trim() });
      setText("");
      loadThread();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    setSending(false);
  };

  const createLogin = async () => {
    setCreating(true);
    try {
      await api.post("/agent/one-clients", {
        name: info.client.name, email: form.email, mobile: info.client.mobile,
        password: form.password, crm_client_id: clientId,
      });
      toast.success("Crece One login created — share the credentials with your client");
      loadInfo();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create login"); }
    setCreating(false);
  };

  if (!info) return <div className="py-10 text-center text-stone-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  if (!info.linked) {
    return (
      <div className="max-w-md" data-testid="one-invite-panel">
        <div className="flex items-center gap-2 mb-2"><Smartphone className="w-4 h-4 text-emerald-800" /><span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Crece One App</span></div>
        <h3 className="font-bold text-lg">This client isn't on the app yet</h3>
        <p className="text-sm text-stone-500 mt-1 mb-4">Create their Crece One login — they'll be locked to you as their advisor, get their financial health scores, AI assistant and this chat.</p>
        <div className="space-y-3">
          <Input data-testid="one-invite-email" placeholder="Client email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input data-testid="one-invite-password" placeholder="Set a password (share with client)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <Button data-testid="one-invite-submit" disabled={creating || !form.email || form.password.length < 6} onClick={createLogin}
            className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold px-6">
            {creating ? "Creating…" : "Create Crece One login"}
          </Button>
          <p className="text-xs text-stone-400">Mobile used: {info.client.mobile}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[60vh]" data-testid="one-chat-panel">
      <div className="pb-3 border-b border-stone-200">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-emerald-800"><Smartphone className="w-4 h-4" /> Crece One App · profile {info.completion_pct}%</div>
        {info.scores ? (
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="one-chat-scores">
            <ScoreChip label="Health" v={info.scores.overall} />
            <ScoreChip label="Cash-flow" v={info.scores.cash_flow} />
            <ScoreChip label="Debt" v={info.scores.debt} />
            <ScoreChip label="Protection" v={info.scores.protection} />
            <ScoreChip label="Emergency" v={info.scores.emergency} />
            <ScoreChip label="Goals" v={info.scores.goal_planning} />
          </div>
        ) : <div className="text-xs text-stone-400 mt-1">App profile not started yet</div>}
      </div>
      <div className="flex-1 overflow-y-auto py-4 space-y-2" data-testid="one-chat-messages">
        {thread.length === 0 && <div className="text-center text-xs text-stone-400 py-8">No messages yet — say hello!</div>}
        {thread.map((m) => (
          <div key={m.id} className={`flex ${m.sender === "advisor" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${m.sender === "advisor" ? "bg-emerald-900 text-white rounded-br-sm" : "bg-stone-100 text-stone-900 rounded-bl-sm"}`}>
              {m.text}
              <div className={`text-[10px] mt-1 ${m.sender === "advisor" ? "text-emerald-200" : "text-stone-400"}`}>{new Date(m.at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="pt-3 border-t border-stone-200 flex gap-2">
        <Input data-testid="one-chat-input" placeholder="Message your client…" value={text}
          onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
        <Button data-testid="one-chat-send" onClick={send} disabled={sending || !text.trim()}
          className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white px-5"><Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
